<template>
  <v-sheet
    min-height="400px"
    :color="styles?.theme === 'dark' ? 'black' : 'white'"
    v-if="styles && !loadingFilters && styles.viewMode === 'grid'"
    class="d-flex flex-wrap overflow-y-auto jzl-preview"
  >
    <!-- Grid view -->
    <div
      v-for="vehicle in sortedVehicles()"
      :key="vehicle.model"
      class="jellybeans__card--link cursor-pointer"
      @click="
        disableUrl(vehicle.make, vehicle.model, vehicle.count)
          ? ''
          : goToVehiclePage(vehicle.make, vehicle.model)
      "
    >
      <v-img
        class="mx-auto"
        :width="
          styles.image.width.includes('%') ||
          styles.image.height.includes('auto')
            ? '320px'
            : styles.image.width + 'px'
        "
        :max-width="
          styles.image.width.includes('%') ||
          styles.image.height.includes('auto')
            ? styles.image.width
            : styles.image.width + 'px'
        "
        :src="
          vehicle.trim && vehicle.trimImg && vehicle.trimImg.length > 0
            ? vehicle.trimImg
            : customImage(vehicle.make, vehicle.model)
              ? customImage(vehicle.make, vehicle.model)
              : `${apiUrl}?make=${vehicle.make}&model=${vehicle.model}&category=${styles.imageType}`
        "
      >
        <template v-slot:placeholder>
          <v-img
            class="mx-auto"
            height="300"
            max-width="500"
            :src="noVehicleImage"
          ></v-img>
        </template>
      </v-img>
      <div
        class="text-center jellybeans__label"
        :style="{
          color:
            styles.theme === 'dark' && styles.label.color === '#000000'
              ? 'white'
              : styles.label.color,
          fontSize: styles.label.typography.fontSize,
          fontWeight: styles.label.typography.fontWeight,
          lineHeight: styles.label.typography.lineHeight,
        }"
      >
        {{
          settingsStore.placements.placements[props.placementId]
            .vehicleNameType &&
          settingsStore.placements.placements[
            props.placementId
          ].vehicleNameType.includes('Make')
            ? vehicle.make
            : ''
        }}
        {{ vehicle.model }}

        <span v-if="hideWhenZeroInStock(vehicle.make, vehicle.model)">
          {{
            settingsStore.placements.placements[props.placementId]
              .showVehicleCount
              ? `${getCustomCountText(vehicle.make, vehicle.model) ? getCustomCountText(vehicle.make, vehicle.model) : `(${vehicle.count})`}`
              : undefined
          }}
        </span>
      </div>
    </div>
  </v-sheet>
  <!-- Slider view -->
  <div v-if="styles && !loadingFilters && styles.viewMode === 'slider'">
    <Carousel v-bind="carouselConfig">
      <Slide
        v-for="vehicle in sortedVehicles()"
        :key="vehicle.model"
        class="jellybeans__card--link cursor-pointer d-flex flex-column"
        @click="
          disableUrl(vehicle.make, vehicle.model, vehicle.count)
            ? ''
            : goToVehiclePage(vehicle.make, vehicle.model)
        "
      >
        <v-img
          :width="
            styles.image.width.includes('%') ||
            styles.image.height.includes('auto')
              ? '320px'
              : styles.image.width + 'px'
          "
          :max-width="
            styles.image.width.includes('%') ||
            styles.image.height.includes('auto')
              ? styles.image.width
              : styles.image.width + 'px'
          "
          :src="
            vehicle.trim && vehicle.trimImg && vehicle.trimImg.length > 0
              ? vehicle.trimImg
              : customImage(vehicle.make, vehicle.model)
                ? customImage(vehicle.make, vehicle.model)
                : `${apiUrl}?make=${vehicle.make}&model=${vehicle.model}&category=${styles.imageType}`
          "
        >
          <template v-slot:placeholder>
            <v-img
              class="mx-auto"
              height="300"
              max-width="500"
              :src="noVehicleImage"
            ></v-img>
          </template>
        </v-img>
        <div
          class="text-center jellybeans__label"
          :style="{
            color:
              styles.theme === 'dark' && styles.label.color === '#000000'
                ? 'white'
                : styles.label.color,
            fontSize: styles.label.typography.fontSize,
            fontWeight: styles.label.typography.fontWeight,
            lineHeight: styles.label.typography.lineHeight,
          }"
        >
          {{
            settingsStore.placements.placements[props.placementId]
              .vehicleNameType &&
            settingsStore.placements.placements[
              props.placementId
            ].vehicleNameType.includes('Make')
              ? vehicle.make
              : ''
          }}
          {{ vehicle.model }}
          <span v-if="hideWhenZeroInStock(vehicle.make, vehicle.model)">
            {{
              settingsStore.placements.placements[props.placementId]
                .showVehicleCount
                ? `${getCustomCountText(vehicle.make, vehicle.model) ? getCustomCountText(vehicle.make, vehicle.model) : `(${vehicle.count})`}`
                : undefined
            }}
          </span>
        </div>
      </Slide>

      <template #addons>
        <Navigation class="mx-20" />
      </template>
    </Carousel>
  </div>
  <v-sheet height="400px" v-else-if="loadingFilters">
    <v-progress-circular
      color="primary"
      indeterminate
      class="mt-8 mx-auto d-block"
    ></v-progress-circular>
  </v-sheet>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/settings'
import { useVehiclesStore } from '@/stores/vehicles'
import { onMounted, ref, onBeforeUnmount } from 'vue'
import 'vue3-carousel/carousel.css'
import { Carousel, Slide, Navigation } from 'vue3-carousel'

const carouselConfig = {
  itemsToShow: 5,
  itemsToScroll: 1,
  wrapAround: true,
  breakpoints: {
    300: { itemsToShow: 1 },
    640: { itemsToShow: 2 },
    768: { itemsToShow: 4 },
    900: { itemsToShow: 5 },
  }
}

import noVehicleImage from '@/assets/no-vehicle-image.png'

const props = defineProps<{
  placementId: string
  loadingFilters: boolean
}>()

const settingsStore = useSettingsStore()
const vehicleStore = useVehiclesStore()

const styles = ref()

const apiUrl = ref()

onMounted(async () => {
  styles.value = settingsStore.placements.placements[props.placementId]
  apiUrl.value = import.meta.env.VITE_VEHICLE_IMG_URL
  await vehicleStore.getVehiclesData()
})

onBeforeUnmount(() => {
  vehicleStore.vehiclesData = []
})

const getCustomCountText = (make: string, model: string) => {
  const filterArray =
    settingsStore.placements.placements[props.placementId]['filter']

  if (Array.isArray(filterArray)) {
    const item = filterArray.find(
      (item: any) => item.make === make && item.model === model,
    )
    return item?.settings?.customCountText || ''
  }

  return '' // Return empty string if 'filter' is not an array
}

const sortedVehicles = () => {
  // if user has dragged to reorder the list, return that reordered list.
  if (settingsStore.placements.placements[props.placementId].usedDrag) {
    return vehicleStore.vehiclesData
  }

  if (styles.value.sort === 'asc') {
    return vehicleStore.vehiclesData.sort((a, b) => a.count - b.count)
  }
  if (styles.value.sort === 'desc') {
    return vehicleStore.vehiclesData.sort((a, b) => b.count - a.count)
  }
  return vehicleStore.vehiclesData
}

const hideWhenZeroInStock = (make: string, model: string) => {
  const filterArray =
    settingsStore.placements.placements[props.placementId]['filter']

  if (Array.isArray(filterArray)) {
    const item = filterArray.find(
      (item: any) => item.make === make && item.model === model,
    )
    if (
      (item && item.settings && item.settings.hideCountIfZero) ||
      (item && item.count !== 0)
    ) {
      return true
    } else {
      return false
    }
  }

  return false
}

const customImage = (make: string, model: string) => {
  const filterArray =
    settingsStore.placements.placements[props.placementId]['filter']

  if (Array.isArray(filterArray)) {
    const item = filterArray.find(
      (item: any) => item.make === make && item.model === model,
    )
    return item?.settings?.customImg || null
  }

  return null // or handle the case when it's not an array
}

const goToVehiclePage = (make: string, model: string) => {
  const item = settingsStore.placements.placements[props.placementId][
    'filter'
  ].find((item: any) => item.make === make && item.model === model)
  if (item && item.settings.customUrl.length > 0) {
    window.open("https://" + item.settings.customUrl, '_blank')
    return
  }
  if (
    settingsStore.placements.placements[props.placementId].baseUrl.length === 0
  )
    return
  const url = buildUrl(make, model)
  window.open(url, '_blank')
}

const buildUrl = (make: string, model: string): string => {
  const prefix = settingsStore.placements.placements[
    props.placementId
  ].baseUrl.includes('https://')
    ? ''
    : 'https://'

  const structure = `/models-${make}-${model ? model.replace('-', '--') : ''}`

  return (
    prefix +
    settingsStore.placements.placements[props.placementId].baseUrl +
    structure
  )
}

const disableUrl = (make: string, model: string, count: number) => {
  const filterArray =
    settingsStore.placements.placements[props.placementId]['filter']

  if (Array.isArray(filterArray)) {
    const item = filterArray.find(
      (item: any) => item.make === make && item.model === model,
    )

    if (
      (item && item.settings.disableWhenCountIsZero && count === 0) ||
      (item && !item.settings.hideCountIfZero && count === 0)
    ) {
      return true
    }
  }
  return false
}
</script>

<style>
.jellybeans__card--link:hover .jellybeans__label {
  color: v-bind(
    'settingsStore.placements.placements[props.placementId].label.hoverColor'
  ) !important;
}

.carousel {
  padding: 40px;
}
</style>
